import { Alert, Button, Stack, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { publicUpload } from '@/api/client'

const MAX_PHOTOS = 3

/**
 * Lets a customer add a photo or a spoken note to their feedback. Optional;
 * each file is sent as soon as it is chosen, and the form is still sent
 * without them.
 */
export function FeedbackAttachments({ token }: { token: string }) {
  const [photos, setPhotos] = useState(0)
  const [voice, setVoice] = useState(false)
  const [busy, setBusy] = useState(false)
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])

  const canRecord =
    typeof window !== 'undefined' &&
    'MediaRecorder' in window &&
    Boolean(navigator.mediaDevices?.getUserMedia)

  const send = async (kind: 'PHOTO' | 'VOICE', file: Blob, name: string) => {
    setBusy(true)
    setError(null)
    const form = new FormData()
    form.append('kind', kind)
    form.append('file', file, name)
    try {
      await publicUpload(`/api/v1/public/feedback/${token}/attachments`, form)
      if (kind === 'PHOTO') setPhotos((count) => count + 1)
      else setVoice(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be added. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const startRecording = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      chunks.current = []
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data)
      }
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop())
        const type = mediaRecorder.mimeType || 'audio/webm'
        const extension = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'
        void send('VOICE', new Blob(chunks.current, { type }), `voice-note.${extension}`)
      }
      mediaRecorder.start()
      recorder.current = mediaRecorder
      setRecording(true)
    } catch {
      setError('The microphone is not available. You can still send your feedback without a voice note.')
    }
  }

  const stopRecording = () => {
    recorder.current?.stop()
    recorder.current = null
    setRecording(false)
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">Add to your feedback (optional)</Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button component="label" variant="outlined" disabled={busy || photos >= MAX_PHOTOS}>
          {photos >= MAX_PHOTOS ? 'Photos added' : `Add a photo (${photos} of ${MAX_PHOTOS})`}
          <input
            hidden
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) void send('PHOTO', file, file.name)
            }}
          />
        </Button>

        {canRecord ? (
          recording ? (
            <Button variant="contained" color="error" onClick={stopRecording}>
              Stop recording
            </Button>
          ) : (
            <Button variant="outlined" disabled={busy || voice} onClick={() => void startRecording()}>
              {voice ? 'Voice note added' : 'Record a voice note'}
            </Button>
          )
        ) : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  )
}
